import { WorkBook, WorkSheet, read, utils } from '@sheet/core';
import { isEmpty } from 'lodash';
import { Observable } from 'rxjs';

import { FileType } from '@shared/components/files-input/files-input.component';

export type ExcelValidateResult = { errorMessage: string; rowCount?: number };

export interface ValidateRule {
  limit?: number;
  totalColumnsNumber?: number;
  format?: FileType[];
  skipEmptyRow?: boolean;
  /** Treat numeric and other non-string cell values as data while removing blank rows. */
  stringifyCellsForEmptyRowCheck?: boolean;
  exactColumnOrder?: string[];
  serviceValidate$?: (file: File) => Observable<ExcelValidateResult>;
  /**
   * When set, the file is validated against multiple sheets in declaration
   * order. The single-sheet rules above are ignored except for `format`.
   */
  sheets?: SheetValidateRule[];
  /**
   * Optional overrides for the default error strings. Used by multi-sheet
   * validation when callers need wording that differs from the defaults.
   */
  errorMessages?: ValidateErrorMessages;
  /**
   * Match sheet names at the positions declared in `sheets` and stop at the first sheet failure.
   * Defaults to name lookup and legacy error priority for existing callers.
   */
  validateSheetOrder?: boolean;
  /** Collapse header whitespace before comparison. Defaults to trim-only matching for existing callers. */
  normalizeHeaders?: boolean;
}

export interface SheetValidateRule {
  /** Exact sheet name in the workbook. Missing required sheet yields a template error. */
  sheetName: string;
  /** Defaults to required. If false, absent sheets and sheets without data rows are skipped before template checks. */
  required?: boolean;
  /** Expected column names in order. Extra trailing columns remain allowed. */
  exactColumnOrder: string[];
  maxDataRows?: number;
  /** Minimum data rows in the first configured sheet; checked after its name and columns. */
  minDataRows?: number;
}

export interface ValidateErrorMessages {
  templateChanged?: string;
  sizeLimitExceeded?: string;
  noItemImported?: string;
}

const DEFAULT_TEMPLATE_CHANGED_MESSAGE = 'Incorrect Format (File template has been changed).';
const DEFAULT_SIZE_LIMIT_MESSAGE = 'File size limit exceeded.';
const DEFAULT_NO_ITEM_MESSAGE = 'No item was imported.';

export interface ImportExcelRule {
  [key: string]: ValidateRule;
}

const PRIVATE_FILE_ERROR_CODE = 'ECMA-376';

export class ExcelValidateUtil {
  public static validate(file: File, rules: ValidateRule): Observable<ExcelValidateResult> {
    return new Observable(observer => {
      observer.add(() => {
        fileReader.abort();
      });

      const fileReader: FileReader = new FileReader();

      fileReader.onload = (e: any) => {
        try {
          if (!ExcelValidateUtil.isFormatValid(rules?.format, file)) {
            observer.next({
              errorMessage: `Incorrect format (allow only format file .${rules.format
                .map(m => m.toLowerCase())
                .join(' .')}).`
            });
          } else if (rules.sheets?.length) {
            observer.next(ExcelValidateUtil.validateMultiSheetFile(e, rules));
          } else {
            observer.next(ExcelValidateUtil.validateDataFile(e, rules));
          }
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      };

      fileReader.onerror = error => {
        observer.error(error);
      };

      fileReader.readAsArrayBuffer(file);
    });
  }

  public static validateDataFile(event: any, rules: ValidateRule): ExcelValidateResult {
    const arrayBuffer = event.target.result;

    let options: any = { type: 'array' };
    let workbook: WorkBook;

    if (!rules.skipEmptyRow) {
      // if skip empty row don't set limit query row
      options = {
        ...options,
        sheetRows: rules.limit + 2
      };
    }
    try {
      workbook = read(arrayBuffer, options);
    } catch (error) {
      if ((error?.message).includes(PRIVATE_FILE_ERROR_CODE)) {
        return { errorMessage: "Unable to access the file. Please set the file's sensitivity to Public." };
      }

      return { errorMessage: error };
    }

    const worksheetName: string = workbook.SheetNames[0];
    const worksheet: WorkSheet = workbook.Sheets[worksheetName];

    let jsonData: any[] = utils.sheet_to_json(worksheet, { header: 1 });
    let errorMessage = null;

    // No data in the first row of column A display error message "Incorrect format (file template has been changed)."
    const optionHeader: any = {
      type: 'array',
      sheetRows: 1
    };
    const workbookHeader: WorkBook = read(arrayBuffer, optionHeader);
    const worksheetNameHeader: string = workbookHeader.SheetNames[0];
    const worksheetHeader: WorkSheet = workbookHeader.Sheets[worksheetNameHeader];

    const header = utils.sheet_to_json(worksheetHeader, { header: 1 })[0] as string[];

    const stringifyCells = rules.stringifyCellsForEmptyRowCheck;
    if (rules.skipEmptyRow) {
      jsonData = ExcelValidateUtil.removeEmptyRow(jsonData, stringifyCells);
    } else {
      jsonData = ExcelValidateUtil.removeLastEmptyRow(jsonData, rules.totalColumnsNumber, stringifyCells);
    }

    if (!header || !ExcelValidateUtil.isTemplateValid(header, rules)) {
      errorMessage = `Incorrect format (file template has been changed).`;
    } else if (ExcelValidateUtil.isEmptyImport(jsonData)) {
      errorMessage = `No item was imported.`;
    } else if (ExcelValidateUtil.isFileSizeExceeded(jsonData.length, rules.limit)) {
      const maxItemsSize = rules.limit.toLocaleString('en-US', {
        style: 'decimal',
        useGrouping: true
      });
      errorMessage = `File size limit exceeded. Up to ${maxItemsSize} rows.`;
    }

    return { errorMessage: errorMessage, rowCount: jsonData.length - 1 };
  }

  public static removeLastEmptyRow(jsonData: any[], totalColumnsNumber: number, stringifyCells = false): any[] {
    let lastRowData = jsonData.length;
    for (let i = jsonData.length - 1; i >= 0; i--) {
      if (
        jsonData[i]
          .slice(0, totalColumnsNumber)
          .every(cell =>
            stringifyCells
              ? cell === null || cell === undefined || String(cell).trim() === ''
              : !cell || (cell && cell.trim() === '')
          )
      ) {
        lastRowData--;
      } else {
        break;
      }
    }

    return jsonData.slice(0, lastRowData);
  }

  public static removeEmptyRow(jsonData: any[], stringifyCells = false): any[] {
    return jsonData.filter(rowData => {
      return Object.values(rowData).some((cell: any) =>
        stringifyCells
          ? cell !== null && cell !== undefined && String(cell).trim() !== ''
          : cell !== null && cell && cell.trim() !== ''
      );
    });
  }

  public static isFormatValid(fileTypesAllowed: FileType[], file: File): boolean {
    if (isEmpty(fileTypesAllowed) || fileTypesAllowed.length === 0) {
      return true;
    }

    const ext = file.name.match(/\.(\w+)$/);
    const allowType = fileTypesAllowed.find(x => ext && x === ext[1].toUpperCase());

    switch (allowType) {
      case FileType.XLSX:
        return file.type.startsWith('application/vnd.openxmlformats-');
      case FileType.CSV:
        return file.type.startsWith('text/csv');
      default:
        return false;
    }
  }

  public static isTemplateValid(header: string[], rules: ValidateRule): boolean {
    const totalHeaderColumns = header.length;
    const totalColumnsNumber = rules?.totalColumnsNumber ?? rules?.exactColumnOrder?.length;
    const exactColumnOrder = rules?.exactColumnOrder;

    if (!totalColumnsNumber || totalColumnsNumber < 0) {
      return true;
    }

    return exactColumnOrder && exactColumnOrder.length
      ? totalHeaderColumns >= totalColumnsNumber && exactColumnOrder.every((val, idx) => val === header[idx]?.trim())
      : totalHeaderColumns >= totalColumnsNumber;
  }

  private static normalizeHeader(value: string): string {
    return String(value ?? '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  public static isEmptyImport(jsonData: any[]): boolean {
    return jsonData.length < 2 || (jsonData.length >= 2 && jsonData.slice(1).every(row => row.length === 0));
  }

  public static isFileSizeExceeded(dataLength: number, limit: number): boolean {
    if (!limit || limit < 0) {
      return false;
    }
    return dataLength > limit + 1;
  }

  public static validateMultiSheetFile(event: any, rules: ValidateRule): ExcelValidateResult {
    let workbook: WorkBook;
    try {
      workbook = read(event.target.result, { type: 'array' });
    } catch (error) {
      if ((error?.message || '').includes(PRIVATE_FILE_ERROR_CODE)) {
        return { errorMessage: "Unable to access the file. Please set the file's sensitivity to Public." };
      }
      return { errorMessage: error };
    }

    const templateChangedMsg = rules.errorMessages?.templateChanged ?? DEFAULT_TEMPLATE_CHANGED_MESSAGE;
    const sizeLimitMsg = rules.errorMessages?.sizeLimitExceeded ?? DEFAULT_SIZE_LIMIT_MESSAGE;
    const noItemMsg = rules.errorMessages?.noItemImported ?? DEFAULT_NO_ITEM_MESSAGE;
    let sizeExceeded = false;
    let noItemImported = false;
    let firstSheetRowCount = 0;

    const templateError = (): ExcelValidateResult =>
      rules.validateSheetOrder
        ? { errorMessage: templateChangedMsg, rowCount: firstSheetRowCount }
        : { errorMessage: templateChangedMsg };

    for (const [idx, sheetRule] of rules.sheets.entries()) {
      const sheetName = rules.validateSheetOrder ? workbook.SheetNames[idx] : sheetRule.sheetName;
      const worksheet = sheetName ? workbook.Sheets[sheetName] : undefined;
      if (!worksheet) {
        if (sheetRule.required === false) continue;
        return templateError();
      }
      if (sheetRule.required !== false && sheetName !== sheetRule.sheetName) return templateError();

      // Optional sheets must be checked for data before validating their name or columns.
      const sheetRows = utils.sheet_to_json(worksheet, {
        header: 1,
        ...(rules.validateSheetOrder ? { range: 0 } : {})
      }) as unknown[][];
      if (sheetRule.required === false && ExcelValidateUtil.removeEmptyRow(sheetRows.slice(1), true).length === 0) {
        continue;
      }
      if (sheetRule.required === false && sheetName !== sheetRule.sheetName) return templateError();

      const header = (sheetRows[0] || []) as string[];
      if (!ExcelValidateUtil.isMultiSheetTemplateValid(header, sheetRule, rules.normalizeHeaders)) {
        return templateError();
      }

      const dataRows = rules.stringifyCellsForEmptyRowCheck
        ? ExcelValidateUtil.removeEmptyRow(sheetRows.slice(1), true)
        : ExcelValidateUtil.removeEmptyRow(sheetRows).slice(1);
      if (idx === 0) {
        firstSheetRowCount = dataRows.length;
        if (sheetRule.minDataRows != null && dataRows.length < sheetRule.minDataRows) {
          // Ordered template validation stops at this sheet before moving on to the next.
          if (rules.validateSheetOrder) return { errorMessage: noItemMsg, rowCount: firstSheetRowCount };
          noItemImported = true;
        }
      }
      if (sheetRule.maxDataRows != null && dataRows.length > sheetRule.maxDataRows) {
        sizeExceeded = true;
      }
    }

    if (sizeExceeded) return { errorMessage: sizeLimitMsg, rowCount: firstSheetRowCount };
    if (noItemImported) return { errorMessage: noItemMsg, rowCount: firstSheetRowCount };
    return { errorMessage: null, rowCount: firstSheetRowCount };
  }

  public static isMultiSheetTemplateValid(
    header: string[],
    sheetRule: SheetValidateRule,
    normalizeHeaders = false
  ): boolean {
    const expected = sheetRule.exactColumnOrder;
    if (!expected?.length) return true;

    return normalizeHeaders
      ? ExcelValidateUtil.isTemplateValid(
          header.map(value => ExcelValidateUtil.normalizeHeader(value)),
          {
            exactColumnOrder: expected.map(value => ExcelValidateUtil.normalizeHeader(value))
          }
        )
      : header.length >= expected.length && expected.every((column, idx) => column === (header[idx] || '').trim());
  }
}
